import {
  Badge,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@repo/ui";
import { Info } from "lucide-react";
import { PermissionsEditor } from "@/components/settings/permissions-editor";
import { NON_OVERRIDABLE_ROLES, ROLE_DISPLAY_NAMES } from "@/lib/permissions";
import type { Role } from "@/lib/permissions";

interface UserPermissionsDialogProps {
  user: { id: string; name: string; role: string } | null;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UserPermissionsDialog({
  user,
  open,
  onOpenChange,
}: UserPermissionsDialogProps) {
  const role = user?.role as Role | undefined;
  const isNonOverridable = role ? NON_OVERRIDABLE_ROLES.has(role) : false;

  if (!user || !role) return null;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[720px] max-h-[85vh] flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            Manage Permissions
            <Badge variant="secondary" className="font-normal">
              {ROLE_DISPLAY_NAMES[role]}
            </Badge>
          </DialogTitle>
          <DialogDescription>
            Customize permissions for {user.name}.
          </DialogDescription>
        </DialogHeader>

        {isNonOverridable ? (
          <div className="flex items-center gap-3 rounded-lg border border-blue-200 bg-blue-50 p-4">
            <Info className="h-5 w-5 text-blue-600 shrink-0" />
            <p className="text-sm text-blue-800">
              The <strong>Admin</strong> role always has full access to all
              permissions. This cannot be overridden.
            </p>
          </div>
        ) : (
          <PermissionsEditor
            key={role}
            role={role}
            description={
              <>
                Changes apply to all <strong>{ROLE_DISPLAY_NAMES[role]}</strong>{" "}
                users in this pharmacy.
              </>
            }
          />
        )}
      </DialogContent>
    </Dialog>
  );
}
